import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { LayoutDashboard, CreditCard, History, Send, Clock, BookOpen, LifeBuoy, Code, Settings as SettingsIcon, X, LogOut, Menu, Users, Inbox, QrCode, ListChecks, ShieldCheck } from "lucide-react";
import { useAuth } from "@/lib/auth";
import NotificationsBell from "@/components/NotificationsBell";

const PANEL_NAV = [
    { to: "/partner/panel", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/partner/panel/virtual-account", label: "My Virtual Account", icon: CreditCard },
    { to: "/partner/panel/beneficiaries", label: "Beneficiaries", icon: Users },
    { to: "/partner/panel/create-payin", label: "Create PayIn", icon: QrCode },
    { to: "/partner/panel/payin-history", label: "PayIn History", icon: ListChecks },
    { to: "/partner/panel/collect", label: "Collect", icon: Inbox },
    { to: "/partner/panel/funding", label: "Funding History", icon: History },
    { to: "/partner/panel/send", label: "Send Payout", icon: Send },
    { to: "/partner/panel/payouts", label: "Payout History", icon: Clock },
    { to: "/partner/panel/statement", label: "Statement", icon: BookOpen },
    { to: "/partner/panel/support", label: "Support", icon: LifeBuoy },
    { to: "/partner/panel/api", label: "API & Webhooks", icon: Code },
    { to: "/partner/panel/webhook-signing", label: "Webhook Signing", icon: ShieldCheck },
    { to: "/partner/panel/settings", label: "Settings", icon: SettingsIcon },
];

/**
 * Generic payouts-portal sidebar. Lives at /partner/panel/* so we don't
 * collide with the existing /partner/* dashboard. The 'X' top-left
 * returns the user to the main partner shell.
 *
 * Vendor identity is intentionally never surfaced to partners — the
 * brand mark here is our own 'Payouts Portal', and the partner's own
 * business name shows on the top-right.
 *
 * Desktop (≥lg): persistent 220px left sidebar.
 * Mobile (<lg): hamburger in the sticky top bar opens an off-canvas drawer.
 */
export default function PartnerPanelShell() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const business = user?.partner?.business_name || "Partner";
    const initial = (business[0] || "P").toUpperCase();

    const NavInner = ({ onClick }) => (
        <>
            <div className="px-4 py-4 border-b border-slate-100">
                <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-600 to-blue-600 flex items-center justify-center text-white font-bold text-sm">{initial}</div>
                    <div className="min-w-0">
                        <div className="font-bold text-sm leading-tight truncate" title={business}>{business}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5">Payouts Portal</div>
                    </div>
                </div>
            </div>
            <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
                {PANEL_NAV.map((it) => (
                    <NavLink key={it.to} to={it.to} end={it.end} onClick={onClick}
                        data-testid={`panel-nav-${it.label.toLowerCase().replace(/\s|&/g, "")}`}
                        className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition ${
                            isActive ? "bg-indigo-50 text-indigo-700 font-medium" : "text-slate-700 hover:bg-slate-50"
                        }`}>
                        <it.icon className="w-4 h-4" /> {it.label}
                    </NavLink>
                ))}
            </nav>
            <div className="p-2 border-t border-slate-100">
                <div className="px-3 py-1 mb-1">
                    <div className="text-[10px] text-slate-400">Signed in</div>
                    <div className="text-xs font-medium truncate">{user?.email}</div>
                </div>
                <button data-testid="panel-logout"
                        onClick={async () => { onClick?.(); await logout(); nav("/partner/login"); }}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs text-slate-600 hover:bg-slate-50">
                    <LogOut className="w-3.5 h-3.5" /> Sign out
                </button>
            </div>
        </>
    );

    return (
        <div className="flex min-h-screen bg-[#F8FAFC]" data-testid="partner-panel-shell">
            {/* Desktop sidebar */}
            <aside className="hidden lg:flex flex-col w-[220px] border-r border-slate-200 bg-white sticky top-0 h-screen">
                <NavInner />
            </aside>

            {/* Mobile drawer — off-canvas */}
            {mobileOpen && (
                <div data-testid="panel-mobile-overlay"
                    className="fixed inset-0 bg-black/40 z-40 lg:hidden"
                    onClick={() => setMobileOpen(false)} />
            )}
            <aside data-testid="panel-mobile-drawer"
                className={`fixed inset-y-0 left-0 w-[260px] z-50 bg-white border-r border-slate-200 flex flex-col transform transition-transform lg:hidden ${
                    mobileOpen ? "translate-x-0" : "-translate-x-full"
                }`}>
                <button data-testid="panel-mobile-close"
                        onClick={() => setMobileOpen(false)}
                        className="absolute top-3 right-3 w-7 h-7 rounded-md hover:bg-slate-100 flex items-center justify-center">
                    <X className="w-4 h-4" />
                </button>
                <NavInner onClick={() => setMobileOpen(false)} />
            </aside>

            <main className="flex-1 min-w-0">
                <div className="sticky top-0 z-30 bg-white border-b border-slate-200 flex items-center px-4 py-3">
                    {/* Mobile hamburger */}
                    <button data-testid="panel-mobile-menu"
                            onClick={() => setMobileOpen(true)}
                            className="lg:hidden w-8 h-8 rounded-md border border-slate-200 flex items-center justify-center hover:bg-slate-50 mr-3">
                        <Menu className="w-4 h-4" />
                    </button>
                    {/* Desktop close */}
                    <button onClick={() => nav("/partner")}
                            data-testid="panel-close"
                            className="hidden lg:flex w-8 h-8 rounded-md border border-slate-200 items-center justify-center hover:bg-slate-50 mr-3">
                        <X className="w-4 h-4" />
                    </button>
                    <div className="font-bold text-sm text-indigo-700">Payouts Portal</div>
                    <div className="ml-auto flex items-center gap-2 min-w-0">
                        <NotificationsBell />
                        <div className="text-xs text-slate-500 font-mono truncate max-w-[130px] sm:max-w-[280px]">{business}</div>
                    </div>
                </div>
                <Outlet />
            </main>
        </div>
    );
}
